import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiFetch, ApiError } from '../lib/api';

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const authToken = localStorage.getItem('blueprint_token');

  const [result, setResult] = useState<
    | { kind: 'loading' }
    | { kind: 'success'; projectId: string | null }
    | { kind: 'error'; message: string }
    | { kind: 'unauthenticated' }
  >({ kind: 'loading' });

  useEffect(() => {
    if (!token || !authToken) return;

    let cancelled = false;
    apiFetch<{ ok: boolean; projectId?: string }>(`/invitations/${token}/accept`, { method: 'POST' })
      .then(data => {
        if (!cancelled) setResult({ kind: 'success', projectId: data.projectId ?? null });
      })
      .catch(err => {
        if (cancelled) return;
        // A stale/invalid session token in localStorage passes the presence
        // check above but is correctly rejected by the server (401). Branch
        // on status first — before the message-text fallback below — because
        // authenticate()'s own rejection message is literally "Invalid or
        // expired token", which the substring match below would otherwise
        // misclassify as the INVITATION being expired rather than the
        // session. Same remedy as the !authToken case (sign in, land back
        // here), so reuse that prompt rather than a plain error banner.
        if (err instanceof ApiError && err.status === 401) {
          setResult({ kind: 'unauthenticated' });
          return;
        }
        const msg = err instanceof Error ? err.message : 'Failed to accept invitation';
        if (msg.includes('expired')) {
          setResult({ kind: 'error', message: 'This invitation has expired. Please ask the project owner to resend it.' });
        } else if (msg.includes('not found')) {
          setResult({ kind: 'error', message: 'This invitation was not found or has already been used.' });
        } else {
          setResult({ kind: 'error', message: msg });
        }
      });
    return () => { cancelled = true; };
  }, [token, authToken]);

  const containerStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', padding: 24,
    background: 'var(--bg)', fontFamily: 'var(--font-body)',
  };

  const cardStyle: React.CSSProperties = {
    maxWidth: 440, width: '100%', padding: 32,
    borderRadius: 'var(--radius-xl)', background: 'var(--surface)',
    border: '1px solid var(--border)', textAlign: 'center',
  };

  // No token — render error directly (no effect needed)
  if (!token) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--error)', marginBottom: 8 }}>
            Unable to accept invitation
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
            No invitation token provided
          </p>
          <button
            onClick={() => navigate('/projects')}
            style={{
              padding: '10px 24px', borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)',
              fontWeight: 500, fontSize: 14, cursor: 'pointer',
            }}
          >
            Go to Projects
          </button>
        </div>
      </div>
    );
  }

  // Not logged in — render login prompt directly. Also reached when the
  // request fired (a token WAS present in localStorage) but the server
  // rejected it as stale/invalid (401) — same remedy either way.
  if (!authToken || result.kind === 'unauthenticated') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
            Sign in to accept
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
            {result.kind === 'unauthenticated'
              ? 'Your session has expired. Please sign in again to accept this project invitation.'
              : 'You need to sign in or create an account to accept this project invitation.'}
          </p>
          <button
            onClick={() => navigate(`/login?redirect=${encodeURIComponent(`/accept-invite?token=${token}`)}`)}
            style={{
              padding: '10px 24px', borderRadius: 'var(--radius-sm)',
              border: 'none', background: 'var(--primary)', color: '#fff',
              fontWeight: 600, fontSize: 14, cursor: 'pointer',
            }}
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  if (result.kind === 'loading') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <p style={{ fontSize: 16, color: 'var(--text)' }}>Accepting invitation...</p>
        </div>
      </div>
    );
  }

  if (result.kind === 'success') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--success)', marginBottom: 8 }}>
            Invitation accepted!
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
            You have been added to the project team.
          </p>
          <button
            onClick={() => navigate(result.projectId ? `/projects/${result.projectId}` : '/projects')}
            style={{
              padding: '10px 24px', borderRadius: 'var(--radius-sm)',
              border: 'none', background: 'var(--primary)', color: '#fff',
              fontWeight: 600, fontSize: 14, cursor: 'pointer',
            }}
          >
            Go to Project
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--error)', marginBottom: 8 }}>
          Unable to accept invitation
        </h2>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
          {result.message}
        </p>
        <button
          onClick={() => navigate('/projects')}
          style={{
            padding: '10px 24px', borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)',
            fontWeight: 500, fontSize: 14, cursor: 'pointer',
          }}
        >
          Go to Projects
        </button>
      </div>
    </div>
  );
}
